import { mpClient } from "./client";
import { remotePlayers } from "./remote-players";
import { mpNormToCanvas } from "../../../lib/multiplayer/normal-sim";
import { mpSnapshotBuffer } from "./snapshot-buffer";

function isTouchGameUi(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  if (document.documentElement.classList.contains("touch-ipad")) return true;
  if (window.matchMedia("(pointer: coarse)").matches && window.matchMedia("(hover: none)").matches) {
    return true;
  }
  return false;
}

export type MpFinisherEntry = { id: string; name: string; timeMs: number };

let finishers: MpFinisherEntry[] = [];
let spectateTargetId: string | null = null;
const remotePoints = new Map<string, Set<number>>();
let localFinished = false;
let localFinishTimeMs = 0;
let lastPointsSent = "";
/** Ignore stale teamSpeedrunPoints from buffered game_state until server confirms reset. */
let coopTeamPointsStaleGuard = false;
let mpSpeedrunCoopApplyFn: ((indices: number[]) => void) | null = null;
let mpSpeedrunCoopRespawnFn: (() => void) | null = null;

export function registerMpSpeedrunCoopHooks(hooks: {
  applyTeamPoints: (indices: number[]) => void;
  respawnFromLaser: () => void;
}): void {
  mpSpeedrunCoopApplyFn = hooks.applyTeamPoints;
  mpSpeedrunCoopRespawnFn = hooks.respawnFromLaser;
}

export function isMpSpeedrunCoop(): boolean {
  return mpClient.gameSubMode === "coop" && mpClient.gameMode === "speedrun";
}

export function isMpSpeedrunRace(): boolean {
  return mpClient.gameSubMode === "ffa" && mpClient.gameMode === "speedrun";
}

/** Match server sim rate (`room-manager` TICK_MS = 1000/30). */
const MP_SPEEDRUN_TICK_HZ = 30;

export function getMpSpeedrunAuthTick(): number | null {
  const frame = mpSnapshotBuffer.latestFrame();
  return frame?.tick ?? null;
}

/** Authoritative speedrun animation time for multiplayer (lasers, moving points, wind). */
export function getMpSpeedrunSimTimeSec(): number {
  const tick = getMpSpeedrunAuthTick();
  if (tick != null && Number.isFinite(tick)) {
    return tick / MP_SPEEDRUN_TICK_HZ;
  }
  const startMs = mpClient.gameStartTime;
  if (startMs != null && Number.isFinite(startMs)) {
    return Math.max(0, (Date.now() - startMs) / 1000);
  }
  return 0;
}

export function applyTeamSpeedrunPoints(indices: number[]): void {
  mpSpeedrunCoopApplyFn?.(indices);
}

/** Apply co-op team points from server snapshots; skips stale pre-respawn state. */
export function applyCoopTeamSpeedrunPointsFromServer(indices: number[] | undefined): void {
  if (!isMpSpeedrunCoop() || !indices) return;
  if (coopTeamPointsStaleGuard) {
    if (indices.length > 0) return;
    coopTeamPointsStaleGuard = false;
  }
  applyTeamSpeedrunPoints(indices);
}

export function respawnMpSpeedrunFromLaser(): void {
  lastPointsSent = "";
  coopTeamPointsStaleGuard = true;
  mpSpeedrunCoopRespawnFn?.();
}

export function resetMpSpeedrunSpectate(): void {
  finishers = [];
  spectateTargetId = null;
  remotePoints.clear();
  localFinished = false;
  localFinishTimeMs = 0;
  lastPointsSent = "";
  coopTeamPointsStaleGuard = false;
}

export function isMpSpeedrunLocalFinished(): boolean {
  return localFinished;
}

export function markMpSpeedrunLocalFinished(timeMs: number): void {
  localFinished = true;
  localFinishTimeMs = timeMs;
  mpClient.sendSpeedrunFinished(timeMs, isTouchGameUi() ? "mobile" : "pc");
  pickDefaultSpectateTarget();
}

export function getMpSpeedrunLocalFinishTime(): number {
  return localFinishTimeMs;
}

export function addMpSpeedrunFinisher(entry: MpFinisherEntry): void {
  if (finishers.some((f) => f.id === entry.id)) return;
  finishers.push(entry);
  finishers.sort((a, b) => a.timeMs - b.timeMs);
  if (localFinished && !spectateTargetId) pickDefaultSpectateTarget();
}

export function getMpSpeedrunFinishers(): MpFinisherEntry[] {
  return finishers;
}

export function setRemoteSpeedrunPoints(playerId: string, indices: number[]): void {
  remotePoints.set(playerId, new Set(indices));
}

export function syncLocalSpeedrunPoints(indices: Iterable<number>): void {
  const key = [...indices].sort((a, b) => a - b).join(",");
  if (key === lastPointsSent) return;
  lastPointsSent = key;
  mpClient.sendSpeedrunPoints([...indices]);
}

export function isMpSpeedrunSpectating(): boolean {
  return localFinished && spectateTargetId != null;
}

export function getMpSpectateTargetId(): string | null {
  return spectateTargetId;
}

export function getMpSpectatePointsSet(): Set<number> | null {
  if (!spectateTargetId) return null;
  return remotePoints.get(spectateTargetId) ?? new Set();
}

function racingPlayerIds(): string[] {
  const finishedIds = new Set(finishers.map((f) => f.id));
  return remotePlayers
    .getRemotes(mpClient.playerId)
    .filter((p) => !finishedIds.has(p.id))
    .map((p) => p.id);
}

function pickDefaultSpectateTarget(): void {
  const ids = racingPlayerIds();
  spectateTargetId = ids[0] ?? null;
}

export function cycleMpSpectateTarget(dir: -1 | 1): void {
  const ids = racingPlayerIds();
  if (ids.length === 0) {
    spectateTargetId = null;
    return;
  }
  if (!spectateTargetId || !ids.includes(spectateTargetId)) {
    spectateTargetId = ids[0]!;
    return;
  }
  const idx = ids.indexOf(spectateTargetId);
  const next = (idx + dir + ids.length) % ids.length;
  spectateTargetId = ids[next]!;
}

export function getMpSpectateTargetName(): string {
  if (!spectateTargetId) return "";
  const p = remotePlayers.getRemotes(mpClient.playerId).find((r) => r.id === spectateTargetId);
  if (p) return p.name;
  return mpClient.room?.players.find((pl) => pl.id === spectateTargetId)?.name ?? "Player";
}

export function getMpSpectateCursorCanvas(cw: number, ch: number): { x: number; y: number } | null {
  if (!spectateTargetId) return null;
  const remotes = remotePlayers.getRemotes(mpClient.playerId);
  const p = remotes.find((r) => r.id === spectateTargetId);
  if (!p) return null;
  return mpNormToCanvas(p.renderX, p.renderY, cw, ch);
}

export function shouldHideMpLocalCursor(): boolean {
  return localFinished;
}

export function shouldSkipMpLocalTrail(localAlive: boolean, localSpectator: boolean): boolean {
  if (localSpectator || !localAlive) return true;
  if (localFinished) return true;
  return false;
}

export function hideMpSpeedrunInGameChrome(): void {
  document.getElementById("mpSpeedrunFinisherList")?.classList.add("hidden");
  document.getElementById("mpSpectateBar")?.classList.add("hidden");
}

export function shouldDrawRemoteMpCursors(): boolean {
  if (isMpSpeedrunSpectating()) return false;
  if (isMpSpeedrunRace()) {
    return mpClient.speedrunRaceShowCursors !== false;
  }
  return true;
}

export function shouldDrawRemoteMpTrails(): boolean {
  return shouldDrawRemoteMpCursors();
}
